import ArticleLayout from "@/components/feature/ArticleLayout";
import { Link } from "react-router-dom";

const faqs = [
  { q: "Wie lange dauert der Versand von OOPBUY nach Deutschland?", a: "Die Versanddauer hängt von der gewählten Versandmethode ab. Mit Express-Versand sind es etwa 5-10 Tage, mit Standard-Versand 10-21 Tage. Das OOPBUY Spreadsheet zeigt die voraussichtlichen Lieferzeiten pro Kategorie an." },
  { q: "Welche Versandmethoden bietet OOPBUY an?", a: "OOPBUY bietet verschiedene Versandoptionen an, darunter DHL, FedEx, EMS und günstigere Alternativen. Die genauen Optionen variieren je nach Zielland und Produktkategorie." },
  { q: "Muss ich Zollgebühren für OOPBUY Bestellungen nach Deutschland zahlen?", a: "Ja, bei Bestellungen aus Nicht-EU-Ländern können Zollgebühren und Einfuhrumsatzsteuer anfallen. Informiere dich vorab über die aktuellen Freigrenzen und Zollsätze." },
];

const versandMethoden = [
  { name: "Express (DHL/FedEx)", dauer: "5-10 Tage", kosten: "Höher", empfehlung: "Für dringende Bestellungen" },
  { name: "Standard (EMS)", dauer: "10-15 Tage", kosten: "Mittel", empfehlung: "Gutes Preis-Leistungs-Verhältnis" },
  { name: "Economy", dauer: "15-21 Tage", kosten: "Günstig", empfehlung: "Für preisbewusste Käufer" },
  { name: "Sea Freight", dauer: "30-45 Tage", kosten: "Am günstigsten", empfehlung: "Für sehr große Bestellungen" },
];

export default function VersandGuidePage() {
  return (
    <ArticleLayout
      title1="OOPBUY Versand"
      title2="nach Deutschland"
      title3="Alles was du wissen musst"
      date="12. Juli 2026"
      readTime="9 Minuten"
      tag="Versand"
      seoDescription="Der komplette OOPBUY Versand Guide für Deutschland. Vergleiche Versandmethoden, erfahre alles über Zollgebühren, Lieferzeiten und Tracking — mit dem OOPBUY Spreadsheet immer den Überblick behalten."
      faqs={faqs}
    >
      <p className="text-lg text-foreground-700 leading-relaxed">
        Der Versand ist einer der wichtigsten Aspekte beim Einkauf über OOPBUY. In diesem Guide erklären wir dir alles, was du über Versandmethoden, Lieferzeiten, Zollgebühren und Tracking wissen musst. Mit dem <strong>OOPBUY Spreadsheet</strong> hast du alle Versandinformationen übersichtlich an einem Ort.
      </p>

      <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10 mb-4">
        Versandmethoden im Vergleich
      </h2>
      <p className="text-foreground-700 leading-relaxed">
        Das <strong>OOPBUY Spreadsheet</strong> listet für jede Bestellung die verfügbaren Versandmethoden auf. Je nachdem, ob du Wert auf Geschwindigkeit oder Kosten legst, kannst du die passende Option wählen.
      </p>

      <div className="my-6">
        <img
          src="https://readdy.ai/api/search-image?query=Clean%20minimalist%20shipping%20and%20logistics%20concept%20with%20cardboard%20parcels%20and%20fashion%20items%2C%20light%20wood%20table%20surface%2C%20soft%20natural%20daylight%2C%20modern%20ecommerce%20aesthetic%2C%20organized%20package%20display%2C%20warm%20beige%20and%20cream%20tones%2C%20editorial%20product%20photography%2C%20fresh%20and%20professional%20atmosphere&width=1200&height=700&seq=article-versand-packaging-v2&orientation=landscape"
          alt="OOPBUY Versand nach Deutschland Pakete"
          className="w-full rounded-xl object-cover"
        />
      </div>

      <div className="overflow-x-auto my-6">
        <table className="w-full border-collapse min-w-[500px]">
          <thead>
            <tr className="border-b border-foreground-200">
              <th className="text-left py-3 px-4 text-sm font-semibold text-foreground-800">Methode</th>
              <th className="text-center py-3 px-4 text-sm font-semibold text-foreground-800">Dauer</th>
              <th className="text-center py-3 px-4 text-sm font-semibold text-foreground-800">Kosten</th>
              <th className="text-center py-3 px-4 text-sm font-semibold text-foreground-800">Empfehlung</th>
            </tr>
          </thead>
          <tbody>
            {versandMethoden.map((m, i) => (
              <tr key={i} className="border-b border-foreground-100">
                <td className="py-3 px-4 text-sm text-foreground-700 font-medium">{m.name}</td>
                <td className="py-3 px-4 text-sm text-foreground-600 text-center">{m.dauer}</td>
                <td className="py-3 px-4 text-sm text-foreground-600 text-center">{m.kosten}</td>
                <td className="py-3 px-4 text-sm text-foreground-500 text-center">{m.empfehlung}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10 mb-4">
        Zoll und Steuern bei Lieferungen nach Deutschland
      </h2>
      <p className="text-foreground-700 leading-relaxed">
        Ein oft unterschätzter Aspekt beim Einkauf über OOPBUY sind die Zollgebühren. Als Faustregel gilt: Bestellungen mit einem Warenwert über 150 Euro unterliegen der Einfuhrumsatzsteuer und möglicherweise zusätzlichen Zollgebühren. Das <strong>OOPBUY Spreadsheet</strong> hilft dir, den Gesamtwert deiner Bestellung im Blick zu behalten, damit du nicht von unerwarteten Zusatzkosten überrascht wirst.
      </p>

      <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10 mb-4">
        Tracking und Sendungsverfolgung
      </h2>
      <p className="text-foreground-700 leading-relaxed">
        Sobald deine Bestellung versendet wurde, erhältst du eine Tracking-Nummer. Mit dieser kannst du den Status deiner Lieferung jederzeit verfolgen. Das <strong>OOPBUY Spreadsheet</strong> enthält einen Bereich, in dem du Tracking-Nummern und Lieferstatus für alle deine Bestellungen dokumentieren kannst — so behältst du immer den Überblick.
      </p>

      <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10 mb-4">
        Tipps für einen reibungslosen Versand
      </h2>
      <ul className="space-y-3 list-none pl-0 text-foreground-700 my-6">
        {[
          "Bestelle rechtzeitig, besonders vor Feiertagen oder Sale-Events",
          "Fasse mehrere Produkte in einer Sendung zusammen, um Versandkosten zu sparen",
          "Überprüfe deine Lieferadresse sorgfältig — ein kleiner Fehler kann zu großen Verzögerungen führen",
          "Dokumentiere alle Tracking-Nummern im OOPBUY Spreadsheet",
          "Informiere dich über die aktuellen Zollbestimmungen für dein Zielland",
        ].map((tip, i) => (
          <li key={i} className="flex items-start gap-2">
            <i className="ri-check-line text-primary-600 w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
            <span className="text-sm">{tip}</span>
          </li>
        ))}
      </ul>

      <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10 mb-4">
        Häufige Fragen zum Versand
      </h2>
      <div className="space-y-4 my-6">
        {faqs.map((faq, i) => (
          <div key={i} className="bg-background-100 rounded-xl p-5 border border-foreground-100/30">
            <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2">{faq.q}</h3>
            <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
          </div>
        ))}
      </div>

      <div className="mt-10 pt-8 border-t border-foreground-200">
        <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-4">Weiterführende Ressourcen</h3>
        <div className="flex flex-wrap gap-3">
          <Link to="/" className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-50 text-primary-700 rounded-full text-sm font-medium hover:bg-primary-100 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-home-line w-4 h-4 flex items-center justify-center"></i>
            Zur Startseite
          </Link>
          <Link to="/einkaufsguide" className="inline-flex items-center gap-1.5 px-4 py-2 bg-background-100 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-shopping-bag-line w-4 h-4 flex items-center justify-center"></i>
            Einkaufsguide
          </Link>
          <Link to="/oopbuy-sicherheit-datenschutz-guide" className="inline-flex items-center gap-1.5 px-4 py-2 bg-background-100 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-shield-check-line w-4 h-4 flex items-center justify-center"></i>
            Sicherheit Guide
          </Link>
        </div>
      </div>
    </ArticleLayout>
  );
}